import { useCallback } from "react";
import { useQuery } from "@apollo/client";
import { FETCH_REQUEST_TYPES } from "../graphql/query/RequestType";

/**
 * Loads the canonical Request Type list (Primary / Secondary / …) from
 * the API so callers don't need to hard-code ids client-side.
 *
 * Mirrors `useOfferStatus`:
 *   - thin wrapper around a single `useQuery`,
 *   - exposes the raw list plus `findRequestTypeById` / `findByName`
 *     callbacks for components that just need to resolve one entry.
 *
 * The deduplicated source of truth is `api/util/requestTypes.js`. By
 * pulling it through GraphQL we avoid the drift bug that previously
 * had the Contact Search UI sending `value: 3` for "Secondary" when
 * the actual id is `2`.
 */
const useRequestTypes = () => {
  const {
    data: { requestTypes: { edges = [] } = {} } = {},
    loading,
    error,
  } = useQuery(FETCH_REQUEST_TYPES, { variables: { input: {} } });

  const findRequestTypeById = useCallback(
    id => edges.find(type => type.id === id),
    [edges],
  );

  const findRequestTypeByName = useCallback(
    name => edges.find(type => type.name === name),
    [edges],
  );

  return {
    error,
    loading,
    requestTypes: edges,
    findRequestTypeById,
    findRequestTypeByName,
  };
};

export default useRequestTypes;
